import React, { useState, useEffect } from 'react'
import {
  T, Avatar, RatingBadge, TierBadge, tierFromProb, pct, confColor, edgeColor, probColor,
  Sheet, Spinner, Stat, bookName, fmtOdds,
} from '../nfl/ui'
import { HitBars, matchupRead, propLine, plainRead } from './cards'
import { getCFBPlayer } from './api'

// Rich player modal — the CFB analog of the NFL player modal, built from /cfb/player:
// identity, every projected prop with its factor breakdown + recent-form hit history,
// and a recent game-log table. Opens from any card/row via its cfbd_id.

function FactorBar({ label, mult }) {
  const pctv = mult != null ? Math.round((mult - 1) * 100) : 0
  const col = pctv > 0 ? T.green : pctv < 0 ? T.red : T.muted
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6 }}>
      <span style={{ width: 96, color: T.muted, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 6, background: T.card3, borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: T.border2 }} />
        <div style={{ position: 'absolute', top: 0, bottom: 0, background: col, borderRadius: 3,
          ...(pctv >= 0 ? { left: '50%', width: `${Math.min(50, Math.abs(pctv) * 2)}%` }
                        : { right: '50%', width: `${Math.min(50, Math.abs(pctv) * 2)}%` }) }} />
      </div>
      <span style={{ width: 42, textAlign: 'right', fontFamily: T.mono, fontWeight: 700, color: col, flexShrink: 0 }}>
        {pctv >= 0 ? '+' : ''}{pctv}%
      </span>
    </div>
  )
}

function PropBlock({ p, pos }) {
  const isTD = p.prop === 'anytime_td'
  const tier = tierFromProb(p.prob, p.confidence)
  const hh = p.hit_hist
  const mr = matchupRead(p.def_adj)
  const dd = p.def_detail
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 13, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RatingBadge tier={tier} size={30} />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>
              {isTD ? 'Anytime TD' : (p.book_line != null ? `${p.market} O${p.book_line}` : p.market)}
            </div>
            <div style={{ fontSize: 10.5, color: T.faint }}>{mr.label} matchup · conf {p.confidence}</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 20, color: probColor(p.prob), lineHeight: 1 }}>{pct(p.prob)}</div>
          <div style={{ fontSize: 8.5, color: T.muted }}>{isTD ? 'TO SCORE' : 'TO HIT'}</div>
        </div>
      </div>

      <div style={{ fontSize: 12, lineHeight: 1.35, marginBottom: 10,
        color: (tier === 'PASS' || tier === 'WATCH') ? T.amber : (tier === 'ELITE' || tier === 'BEST BET' || tier === 'STRONG') ? T.green : T.muted }}>
        {plainRead(tier, isTD)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 10 }}>
        <Stat label="Projection" value={p.proj != null ? p.proj : '—'} />
        <Stat label={isTD ? 'Score line' : (p.book_line != null ? 'Book line' : 'Model line')} value={propLine(p)} />
        <Stat label="Confidence" value={p.confidence || '—'} color={confColor(p.confidence)} />
      </div>

      {!isTD && (
        <>
          <div style={{ fontSize: 10, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>What moves it</div>
          <FactorBar label="Def matchup" mult={p.def_adj} />
          <FactorBar label="Game script" mult={p.gs_adj} />
        </>
      )}

      {/* Real defense-vs-position stat (the number behind the Matchup chip) */}
      {dd && (
        <div style={{ marginTop: 10, background: T.card3, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 10, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
            Opponent D vs {pos || 'pos'}
          </div>
          <div style={{ fontSize: 12.5, color: T.text, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span><b style={{ fontFamily: T.mono }}>{dd.allowed_pg}</b> {dd.stat}/gm allowed</span>
            <span style={{ color: T.muted }}>#{dd.rank} of {dd.of} toughest</span>
            <span style={{ color: dd.pct_vs_avg > 0 ? T.green : dd.pct_vs_avg < 0 ? T.red : T.muted, fontWeight: 700 }}>
              {dd.pct_vs_avg > 0 ? '+' : ''}{dd.pct_vs_avg}% vs avg
            </span>
          </div>
          <div style={{ fontSize: 10, color: T.faint, marginTop: 3 }}>
            {dd.opp} · FBS avg {dd.league_avg} · higher = softer matchup (good for the over)
          </div>
        </div>
      )}

      {hh && hh.n > 0 && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
              Last {hh.n} · cleared this line
            </div>
            <HitBars games={hh.games} />
          </div>
          <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 15, color: probColor(hh.n ? hh.w / hh.n : 0) }}>{hh.w}/{hh.n}</span>
        </div>
      )}

      {p.book_line != null && !p.line_suspect && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: T.muted2, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: T.green, fontWeight: 700 }}>💰 {bookName(p.book_used)} {fmtOdds(p.book_over_odds)}</span>
          <span>O{p.book_line}</span>
          {p.edge_pct != null && <span style={{ color: edgeColor(p.edge_pct), fontWeight: 800 }}>{p.edge_pct >= 0 ? '+' : ''}{p.edge_pct}% edge</span>}
          {p.num_books ? <span style={{ color: T.faint }}>· {p.num_books} books</span> : null}
        </div>
      )}
      {p.line_suspect && (
        <div style={{ marginTop: 8, fontSize: 11, color: T.amber }}>⚠ This book's line disagrees with the market — likely a bad feed row.</div>
      )}
      {p.caution && (
        <div style={{ marginTop: 8, fontSize: 11, color: T.amber, lineHeight: 1.4,
          background: T.amberDim, border: `1px solid ${T.amber}`, borderRadius: 8, padding: '7px 9px' }}>
          ⚠ {p.caution}
        </div>
      )}
    </div>
  )
}

const STATCOLS = [
  ['Wk', r => r.week], ['Opp', r => r.opp],
  ['Pa', r => r.pass_yards], ['Ru', r => r.rush_yards], ['Re', r => r.receptions],
  ['ReYd', r => r.rec_yards], ['TD', r => (r.rush_tds || 0) + (r.rec_tds || 0)],
]

function GameLog({ recent }) {
  if (!recent || !recent.length) return null
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: 10, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Recent games</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
          <thead>
            <tr>{STATCOLS.map(([h]) => (
              <th key={h} style={{ textAlign: h === 'Opp' ? 'left' : 'right', color: T.faint, fontWeight: 600, padding: '4px 6px', whiteSpace: 'nowrap' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {recent.slice().reverse().map((r, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${T.border}` }}>
                {STATCOLS.map(([h, f]) => (
                  <td key={h} style={{ textAlign: h === 'Opp' ? 'left' : 'right', padding: '5px 6px',
                    color: h === 'Opp' ? T.muted : T.text, fontFamily: h === 'Opp' || h === 'Wk' ? 'inherit' : T.mono,
                    whiteSpace: 'nowrap', maxWidth: h === 'Opp' ? 90 : 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {h === 'Opp' ? `vs ${f(r)}` : (f(r) ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function PlayerModal({ play, season, week, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (!play) return
    let live = true; setLoading(true); setData(null)
    getCFBPlayer(play.cfbd_id, season, week).then(d => { if (live) { setData(d); setLoading(false) } })
    return () => { live = false }
  }, [play, season, week])
  if (!play) return null
  const ident = data?.player && data.player.name ? data.player : { name: play.name, team: play.team, pos: play.pos, conference: play.conference, opp: play.opp }
  // Fall back to the single tapped play if the detail fetch hasn't populated props yet.
  const plays = (data?.plays && data.plays.length) ? data.plays : [play]
  const best = plays.reduce((a, b) => (b.prob > (a?.prob || 0) ? b : a), plays[0])
  const tier = tierFromProb(best?.prob || play.prob, best?.confidence || play.confidence)
  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '16px 16px 34px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <Avatar name={ident.name} size={48} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 19, fontWeight: 900, color: T.text }}>{ident.name}</div>
            <div style={{ fontSize: 12, color: T.muted }}>
              {[ident.pos, ident.team, ident.conference, ident.opp ? `vs ${ident.opp}` : ''].filter(Boolean).join(' · ')}
            </div>
          </div>
          <RatingBadge tier={tier} size={42} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <TierBadge tier={tier} />
          {data?.games_played ? <span style={{ fontSize: 11, color: T.faint }}>{data.games_played} games this season</span> : null}
        </div>

        {loading ? <Spinner /> : (
          <>
            {plays.map((p, i) => <PropBlock key={i} p={p} pos={ident.pos} />)}
            <GameLog recent={data?.recent} />
          </>
        )}

        <div style={{ marginTop: 16, fontSize: 10.5, color: T.faint, textAlign: 'center', lineHeight: 1.5 }}>
          Confidence = data quality, not the pick · research, not betting advice · 21+
        </div>
      </div>
    </Sheet>
  )
}
